// Hooks do DISCADOR: campanhas, relatório/quadro da fila, presença do vendedor
// e a fila da organização (rota /dialer). Mesmo padrão de use-calls.ts:
//   - `src/api/dialer.ts` guarda o request (fetch + tipos), sem React;
//   - este arquivo guarda as CHAVES e os hooks do TanStack Query;
//   - a tela não importa a função de api: importa o hook.
//
// As chaves abaixo são as que já estavam espalhadas pelas telas, string por
// string — centralizar não é renomear. O React Query invalida por PREFIXO, então
// uma chave que divergisse uma letra viraria dado velho na tela sem erro nenhum.
import { useCallback } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  claimNextDialerLead,
  createCampaign,
  deleteCampaign,
  dialCampaignLead,
  getCampaigns,
  getCampaignStats,
  getDialerAgentAvailability,
  getDialerQueue,
  getNextLead,
  getQueueReport,
  getQueueTotalsByStatus,
  getSellerAvailabilityReport,
  scheduleCampaignLead,
  setDialerAvailability,
  skipCampaignLead,
  updateCampaign,
  type Campaign,
  type CampaignFilter,
  type DialerAgentAvailability,
  type NextLead,
  type QueueScope,
  type RetryPolicy,
} from "@/api/dialer";

// Recorte de fila que o quadro e os totais mandam juntos: mudar um dos dois é
// OUTRA fila, e por isso os dois entram na chave.
export interface QueueSlice {
  scope: QueueScope;
  includeInactive: boolean;
}

// Período já em ISO, do jeito que as telas de relatório montam. `null` = o
// usuário limpou o período; a chave carrega os dois campos como `undefined`,
// exatamente como estava inline nas telas.
export interface IsoRange {
  from: string;
  to: string;
}

// As chaves DESTE domínio. Divergências herdadas que valem leitura — todas
// preservadas string por string, porque centralizar não é consertar:
//
//  - `campaigns` (["campaigns"]) é PREFIXO de `campaignList`: toda mutação de
//    campanha invalida só o prefixo e conta com isso para derrubar as duas
//    listas (a do gestor, "all", e a do vendedor, "ACTIVE").
//  - `campaignStats` (["campaign-stats", id]) fica FORA desse prefixo, de
//    propósito: o console recarrega o progresso à mão ao pedir o próximo lead,
//    e as mutações de campanha não o tocam.
//  - o mesmo GET /v1/reports/queue/by-status é lido sob DUAS chaves sem
//    parentesco: `kanbanTotals` (["queue-kanban-totals", …]) no quadro e
//    `dashboardQueueTotals` (["reports","queue","by-status","dashboard"]) no
//    Dashboard. São duas entradas de cache para o mesmo endpoint com recortes
//    diferentes; unificar seria mudança de comportamento.
//  - o mesmo GET /v1/dialer/availability/agents também é lido sob duas chaves:
//    `agentAvailability` (["dialer-agent-availability"]) no Relatório de Filas
//    e `dashboardAgents` (["dialer","availability","agents"]) no Dashboard.
//    Idem: divergência pré-existente, preservada.
//  - `availabilityDay` tem QUATRO elementos e `availabilityReport` tem cinco,
//    ambas sob o prefixo ["dialer","availability","report"]. A de Chamadas
//    Ativas leva só o `from` (o recorte é sempre o dia inteiro, o `to` não
//    distingue nada) e pede `hourly`; a de Relatório de Vendedores leva o
//    período. Ninguém invalida nenhuma das duas.
//  - `leads` (["dialer-leads"]) não tem consulta NENHUMA lendo por ela: é uma
//    chave só de invalidação, usada por use-leads.ts quando o status dos leads
//    é reprocessado ou o catálogo é restaurado. Fica aqui porque o domínio é
//    este; o valor não mudou.
export const dialerKeys = {
  campaigns: ["campaigns"] as const,
  campaignList: (scope: "all" | "ACTIVE") => ["campaigns", scope] as const,
  campaignStats: (campaignId: string) => ["campaign-stats", campaignId] as const,
  queueReport: ["queue-report"] as const,
  queueReportList: (params: QueueReportParams) => ["queue-report", params] as const,
  kanbanColumn: (status: string, slice: QueueSlice) =>
    ["queue-kanban", status, slice.scope, slice.includeInactive] as const,
  kanbanTotals: (slice: QueueSlice) =>
    ["queue-kanban-totals", slice.scope, slice.includeInactive] as const,
  dashboardQueueTotals: ["reports", "queue", "by-status", "dashboard"] as const,
  agentAvailability: ["dialer-agent-availability"] as const,
  dashboardAgents: ["dialer", "availability", "agents"] as const,
  availabilityDay: (from: string) => ["dialer", "availability", "report", from] as const,
  availabilityReport: (from?: string, to?: string) =>
    ["dialer", "availability", "report", from, to] as const,
  availabilityHourly: (from?: string, to?: string) =>
    ["dialer", "availability", "report", "hourly", from, to] as const,
  queue: ["dialer-queue"] as const,
  leads: ["dialer-leads"] as const,
};

// O recorte que a tabela do Relatório de Filas monta e manda inteiro na chave:
// filtro diferente é OUTRA fila.
export type QueueReportParams = Parameters<typeof getQueueReport>[0];

// --- Campanhas ---

// Uma consulta, duas telas. O escopo é o MESMO valor que ia na chave inline:
// "all" (gestor, sem filtro de status) e "ACTIVE" (vendedor, só as ativas).
export function useCampaigns(scope: "all" | "ACTIVE", options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: dialerKeys.campaignList(scope),
    queryFn: () => getCampaigns(scope === "all" ? undefined : scope),
    enabled: options.enabled,
  });
}

export function useCampaignStats(campaignId: string) {
  return useQuery({
    queryKey: dialerKeys.campaignStats(campaignId),
    queryFn: () => getCampaignStats(campaignId),
  });
}

// A invalidação da lista é a mesma para todo mundo que mexe em campanha, então
// é do hook. Ela devolve a PROMESSA: dois dos chamadores abaixo a retornam do
// `onSuccess` de propósito (o TanStack espera o onSuccess, e é isso que segura
// o botão desabilitado até a lista voltar) e dois a descartam. Essa diferença
// é pré-existente, está preservada, e é por isso que existem hooks separados
// para "ativar/pausar" e para "salvar a regra de retry": os dois chamam
// updateCampaign, mas prometem coisas diferentes ao botão.
function useInvalidateCampaigns() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: dialerKeys.campaigns });
}

// Os `mutationFn` chamam a função de api dentro de uma seta, em vez de passá-la
// direta: o TanStack invoca o mutationFn com (variáveis, contexto), e uma
// função de api que aceite um segundo parâmetro receberia o contexto da mutação
// como se fosse o dado dela.

// Ativar/pausar/concluir. ESPERA a invalidação antes de sair de `isPending`.
export function useSetCampaignStatus(handlers: { onError?: (error: Error) => void } = {}) {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (args: { id: string; status: "ACTIVE" | "PAUSED" | "DONE" }) =>
      updateCampaign(args.id, { status: args.status }),
    onSuccess: () => invalidate(),
    onError: handlers.onError,
  });
}

// Excluir a campanha. Também ESPERA a invalidação — o botão da linha usa
// `isPending` para não deixar clicar duas vezes na mesma linha.
export function useDeleteCampaign(handlers: { onError?: (error: Error) => void } = {}) {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (id: string) => deleteCampaign(id),
    onSuccess: () => invalidate(),
    onError: handlers.onError,
  });
}

// Regra de retry POR CAMPANHA. Aqui a invalidação é descartada e o toast sai na
// hora (a tela não espera a lista voltar) — o oposto dos dois de cima.
export function useSaveCampaignRetryPolicy(
  campaignId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (retryPolicy: RetryPolicy) => updateCampaign(campaignId, { retryPolicy }),
    onSuccess: () => {
      void invalidate();
      handlers.onSuccess?.();
    },
    onError: handlers.onError,
  });
}

export interface NewCampaignInput {
  name: string;
  filter: CampaignFilter;
  retryPolicy?: RetryPolicy;
}

// Criar. A tela limpa o formulário ANTES da invalidação e do toast — ordem
// preservada, por isso o handler entra como parâmetro do hook.
export function useCreateCampaign(
  handlers: { onSuccess?: (campaign: Campaign) => void; onError?: (error: Error) => void } = {},
) {
  const invalidate = useInvalidateCampaigns();
  return useMutation({
    mutationFn: (input: NewCampaignInput) => createCampaign(input),
    onSuccess: (campaign) => {
      handlers.onSuccess?.(campaign);
      void invalidate();
    },
    onError: handlers.onError,
  });
}

// --- Console de discagem de uma campanha ---
//
// As quatro mutações abaixo não tocam em cache NENHUM: quem redesenha a tela é
// o estado local do console (o lead da vez, o callId em curso). São hooks mesmo
// assim para a tela não precisar da função de api — mesmo caso do
// `useCallRecordingDownload` em use-calls.ts.

export function useNextCampaignLead(
  campaignId: string,
  handlers: { onSuccess?: (next: NextLead | null) => void; onError?: (error: Error) => void } = {},
) {
  return useMutation({
    mutationFn: () => getNextLead(campaignId),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}

export function useDialCampaignLead(
  campaignId: string,
  handlers: {
    onSuccess?: (result: { callId: string }) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  return useMutation({
    mutationFn: (campaignLeadId: string) => dialCampaignLead(campaignId, campaignLeadId),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}

export function useSkipCampaignLead(
  campaignId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  return useMutation({
    mutationFn: (campaignLeadId: string) => skipCampaignLead(campaignId, campaignLeadId),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}

export function useScheduleCampaignLead(
  campaignId: string,
  handlers: { onSuccess?: () => void; onError?: (error: Error) => void } = {},
) {
  return useMutation({
    mutationFn: (args: { campaignLeadId: string; scheduledAt: string }) =>
      scheduleCampaignLead(campaignId, args.campaignLeadId, args.scheduledAt),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}

// --- Relatório de Fila (a ordem real, visão do gestor) ---

// Tabela do Relatório de Filas. `placeholderData` mantém a página anterior na
// tela enquanto a nova chega — sem ela a tabela pisca vazia a cada filtro.
export function useQueueReport(params: QueueReportParams) {
  return useQuery({
    queryKey: dialerKeys.queueReportList(params),
    queryFn: () => getQueueReport(params),
    placeholderData: (prev) => prev,
  });
}

// Remover da fila / reativar / excluir NÃO têm hook de mutação: a tela dispara
// as três dentro de um mesmo `try/catch` que toasta e chama este invalidador.
// Elas continuam sendo chamadas direto de `@/api/dialer` — mesmo caso da
// importação de CSV em use-leads.ts. O que se reaproveita daqui é a chave.
export function useInvalidateQueueReport() {
  const queryClient = useQueryClient();
  return useCallback(
    () => void queryClient.invalidateQueries({ queryKey: dialerKeys.queueReport }),
    [queryClient],
  );
}

// --- Quadro da fila (kanban por status) ---

// Uma coluna do quadro: página por `offset` dentro do MESMO status. Página
// curta = fim da coluna — sem isso o "carregar mais" ficaria oferecendo uma
// página que nunca vem.
export function useQueueKanbanColumn(
  status: string,
  slice: QueueSlice,
  options: { pageSize: number; refetchInterval: number },
) {
  const { pageSize } = options;
  return useInfiniteQuery({
    queryKey: dialerKeys.kanbanColumn(status, slice),
    queryFn: ({ pageParam }) =>
      getQueueReport({
        status,
        limit: pageSize,
        offset: pageParam as number,
        scope: slice.scope,
        includeInactive: slice.includeInactive,
      }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) =>
      last.items.length < pageSize ? undefined : pages.length * pageSize,
    refetchInterval: options.refetchInterval,
    // Só as páginas já carregadas são refeitas, e só com a aba à vista: numa
    // coluna com muito "carregar mais", repetir tudo em segundo plano custaria
    // caro sem ninguém olhando.
    refetchIntervalInBackground: false,
  });
}

// O total REAL de cada coluna. Segue o MESMO recorte da listagem, senão a
// coluna anuncia um número que não é o da lista que está mostrando.
export function useQueueKanbanTotals(slice: QueueSlice, options: { refetchInterval: number }) {
  return useQuery({
    queryKey: dialerKeys.kanbanTotals(slice),
    queryFn: () => getQueueTotalsByStatus(slice),
    refetchInterval: options.refetchInterval,
    refetchIntervalInBackground: false,
  });
}

// O mesmo endpoint no Dashboard, sem recorte (a fila inteira) e sob outra
// chave — ver a ressalva na factory acima.
export function useDashboardQueueTotals(
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: dialerKeys.dashboardQueueTotals,
    queryFn: () => getQueueTotalsByStatus(),
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
  });
}

// --- Presença do vendedor no discador ---

// Monitor da fila (Relatório de Filas): quem está disponível agora.
export function useDialerAgentAvailability(
  options: { refetchInterval?: number; refetchOnWindowFocus?: "always" } = {},
) {
  return useQuery({
    queryKey: dialerKeys.agentAvailability,
    queryFn: () => getDialerAgentAvailability(),
    refetchInterval: options.refetchInterval,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
  });
}

// O mesmo endpoint no Dashboard, sob outra chave — ver a ressalva na factory.
export function useDashboardAgentAvailability(
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: dialerKeys.dashboardAgents,
    queryFn: () => getDialerAgentAvailability(),
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
  });
}

// Chamadas Ativas: presença do DIA com a série por hora, para o painel de
// equipe e a linha do tempo lado a lado.
export function useDayAvailabilityReport(
  day: IsoRange,
  options: { refetchInterval?: number; refetchOnWindowFocus?: boolean } = {},
) {
  return useQuery({
    queryKey: dialerKeys.availabilityDay(day.from),
    queryFn: () => getSellerAvailabilityReport({ ...day, hourly: true }),
    refetchInterval: options.refetchInterval,
    refetchOnWindowFocus: options.refetchOnWindowFocus,
  });
}

// Relatório de Vendedores: o acumulado do período, sem série por hora.
export function useSellersAvailabilityReport(
  range: IsoRange | null,
  options: { enabled?: boolean; refetchInterval?: number } = {},
) {
  return useQuery({
    queryKey: dialerKeys.availabilityReport(range?.from, range?.to),
    queryFn: () => getSellerAvailabilityReport(range ? { from: range.from, to: range.to } : {}),
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
  });
}

// A série por hora vai numa consulta SEPARADA de propósito: é histórico, não
// acompanha o poll do status ao vivo, e só é pedida em período curto.
export function useSellersAvailabilityTimeline(
  range: IsoRange | null,
  options: { enabled?: boolean } = {},
) {
  return useQuery({
    queryKey: dialerKeys.availabilityHourly(range?.from, range?.to),
    queryFn: () =>
      getSellerAvailabilityReport(
        range ? { from: range.from, to: range.to, hourly: true } : { hourly: true },
      ),
    enabled: options.enabled,
  });
}

// --- Discador automático (fila da organização, rota /dialer) ---

// Peek read-only dos próximos da fila (painel lateral). Reflete a ordem real do
// back; a tela revalida a cada avanço (claim/outcome/skip) e passa o ritmo do
// poll — rápido durante a sessão (sincroniza claims de OUTROS vendedores),
// espaçado fora dela. `staleTime` sozinho não busca de novo: é o poll que
// reconstrói o painel, sem claimar nenhum lead.
export function useDialerQueue(limit: number, options: { refetchInterval: number }) {
  return useQuery({
    queryKey: dialerKeys.queue,
    queryFn: () => getDialerQueue(limit),
    staleTime: 15_000,
    refetchInterval: options.refetchInterval,
    refetchOnWindowFocus: "always",
  });
}

// Invalidar a fila é a mesma coisa em todo lugar do discador (o avanço de lead,
// o contador de reabastecimento ao zerar), então mora aqui.
//
// `useCallback` NÃO é enfeite: no discador esta função entra em arrays de
// dependência (o `removeFromVisibleQueue`, o efeito que detecta o lead tomado
// por um colega, o intervalo do contador). Uma identidade nova a cada render
// faria o efeito do contador se recriar dentro do próprio render que ele
// dispara — laço infinito. O `queryClient` do provider é estável, então este
// callback é estável.
export function useInvalidateDialerQueue() {
  const queryClient = useQueryClient();
  return useCallback(
    () => void queryClient.invalidateQueries({ queryKey: dialerKeys.queue }),
    [queryClient],
  );
}

// Serve o próximo lead (claim → IN_FLIGHT). Sem invalidação no hook: quem
// decide o que acontece com o cache é a tela, que já sabe se o lead servido sai
// da lista visível (setQueryData otimista) ou se a fila voltou vazia.
export function useClaimNextDialerLead(
  handlers: {
    onSuccess?: (result: Awaited<ReturnType<typeof claimNextDialerLead>>) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  return useMutation({
    mutationFn: () => claimNextDialerLead(),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}

// Disponibilidade DESTA ABA (o clientId identifica a aba, não o usuário). O
// heartbeat e a baixa na saída da tela continuam imperativos na tela: rodam
// dentro de efeitos/cleanup, onde não há mutação para observar.
export function useSetDialerAvailability(
  clientId: string,
  handlers: {
    onSuccess?: (availability: DialerAgentAvailability) => void;
    onError?: (error: Error) => void;
  } = {},
) {
  return useMutation({
    mutationFn: (available: boolean) => setDialerAvailability(available, clientId),
    onSuccess: handlers.onSuccess,
    onError: handlers.onError,
  });
}
